import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { CreateInsertionOrderRequest, InsertionOrder } from '../../../redux/billing/billing.types';
import {
  useGetInsertionOrderFieldsQuery,
  useCreateInsertionOrderMutation,
  useUpdateInsertionOrderMutation,
  useLazyGetInsertionOrderPreviewQuery,
  useSendInsertionOrderMutation,
} from '../../../redux/billing/billing.endpoints';
import { InsertionOrderTable, DEFAULT_BUDGET_TABLE_STRUCTURE } from './insertion-order-table';
import { DocumentPreviewModal } from './document-preview-modal';

interface AutocompleteInputProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  suggestions?: string[];
  className?: string;
  required?: boolean;
  disabled?: boolean;
  type?: string;
}

const AutocompleteInput = ({
  id,
  value,
  onChange,
  placeholder,
  suggestions = [],
  className,
  required,
  disabled,
  type = 'text',
}: AutocompleteInputProps) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!value || !suggestions.length) {
      setFilteredSuggestions([]);
      return;
    }

    const filtered = suggestions
      .filter(suggestion =>
        suggestion &&
        suggestion.toLowerCase().includes(value.toLowerCase()) &&
        suggestion.toLowerCase() !== value.toLowerCase()
      )
      .slice(0, 5); // Limit to 5 suggestions

    setFilteredSuggestions(filtered);
  }, [value, suggestions]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    setShowSuggestions(newValue.length > 0);
  };

  const handleSuggestionClick = (suggestion: string) => {
    onChange(suggestion);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const handleInputFocus = () => {
    if (value.length > 0 && filteredSuggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        id={id}
        type={type}
        value={value}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        placeholder={placeholder}
        className={className}
        required={required}
        disabled={disabled}
      />
      {showSuggestions && filteredSuggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-40 overflow-y-auto mt-1"
        >
          {filteredSuggestions.map((suggestion, index) => (
            <div
              key={index}
              className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
              onClick={() => handleSuggestionClick(suggestion)}
            >
              {suggestion}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface InsertionOrderModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode?: 'create' | 'edit' | 'view';
  initialData?: InsertionOrder;
  onSuccess?: () => void;
}

export const InsertionOrderModal = ({
  open,
  onOpenChange,
  mode = 'create',
  initialData,
  onSuccess,
}: InsertionOrderModalProps) => {
  const [formData, setFormData] = useState<Omit<CreateInsertionOrderRequest, 'budget'>>({
    advertiser_name: '',
    campaign_name: '',
    start_date: '',
    end_date: '',
    company_name: '',
    client_email: '',
    client_name: '',
    agent_email: '',
    agent_name: '',
    cc_emails: '',
    logo: 'campaign_insights',
    prebill: false,
    include_sla: true,
  });
  const [budgetData, setBudgetData] = useState<string[][]>([]);
  const [currentInsertionOrderId, setCurrentInsertionOrderId] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewData, setPreviewData] = useState<InsertionOrder | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const { data: fieldsData } = useGetInsertionOrderFieldsQuery();

  const [createInsertionOrder, { isLoading: isCreating }] = useCreateInsertionOrderMutation();
  const [updateInsertionOrder, { isLoading: isUpdating }] = useUpdateInsertionOrderMutation();
  const [sendInsertionOrder, { isLoading: isSending }] = useSendInsertionOrderMutation();
  const [triggerPreview, { isLoading: isLoadingPreview }] = useLazyGetInsertionOrderPreviewQuery();

  const isReadOnly = mode === 'view';
  const isSaving = isCreating || isUpdating;

  useEffect(() => {
    if (initialData && open) {
      setFormData({
        advertiser_name: initialData.advertiser_name,
        campaign_name: initialData.campaign_name,
        start_date: initialData.start_date,
        end_date: initialData.end_date,
        company_name: initialData.company_name,
        client_email: initialData.client_email,
        client_name: initialData.client_name,
        agent_email: initialData.agent_email,
        agent_name: initialData.agent_name,
        cc_emails: initialData.cc_emails,
        logo: initialData.logo,
        prebill: initialData.prebill,
        include_sla: initialData.include_sla,
      });
      setBudgetData(initialData.budget || []);
    } else if (!initialData && open && mode === 'create') {
      setFormData({
        advertiser_name: '',
        campaign_name: '',
        start_date: '',
        end_date: '',
        company_name: '',
        client_email: '',
        client_name: '',
        agent_email: '',
        agent_name: '',
        cc_emails: '',
        logo: 'campaign_insights',
        prebill: false,
        include_sla: true,
      });
      setBudgetData(DEFAULT_BUDGET_TABLE_STRUCTURE);
    }
    setValidationErrors({});
    if (open) {
      setCurrentInsertionOrderId(null);
    }
  }, [initialData, open, mode]);

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const validateCCEmails = (ccEmails: string): string | null => {
    if (!ccEmails.trim()) return null;
    const emails = ccEmails.split(',').map(email => email.trim()).filter(email => email.length > 0);
    const invalidEmails = emails.filter(email => !isValidEmail(email));
    if (invalidEmails.length > 0) {
      return `Invalid email${invalidEmails.length > 1 ? 's' : ''}: ${invalidEmails.join(', ')}`;
    }
    return null;
  };

  const validateDates = (startDate: string, endDate: string): { start?: string; end?: string } => {
    const errors: { start?: string; end?: string } = {};
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (end < start) {
        errors.end = 'End date cannot be before start date';
      }
    }
    return errors;
  };

  const validateField = (field: string, value: string | boolean | null): string | null => {
    if (value === null || value === undefined) {
      return null;
    }
    const stringValue = typeof value === 'boolean' ? value.toString() : value;

    switch (field) {
      case 'client_email':
      case 'agent_email':
        if (!stringValue.trim()) return 'This field is required';
        if (!isValidEmail(stringValue)) return 'Please enter a valid email address';
        return null;
      case 'cc_emails':
        return validateCCEmails(stringValue);
      case 'advertiser_name':
      case 'campaign_name':
      case 'start_date':
      case 'end_date':
      case 'company_name':
      case 'client_name':
      case 'agent_name':
        if (!stringValue.trim()) return 'This field is required';
        return null;
      case 'logo':
      case 'prebill':
      case 'include_sla':
        return null;
      default:
        return null;
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    Object.keys(formData).forEach(field => {
      const fieldValue = formData[field as keyof typeof formData];
      if (fieldValue !== null && fieldValue !== undefined) {
        const error = validateField(field, fieldValue);
        if (error !== null && typeof error === 'string') {
          errors[field] = error;
        }
      }
    });
    const dateErrors = validateDates(formData.start_date, formData.end_date);
    if (dateErrors.start) errors.start_date = dateErrors.start;
    if (dateErrors.end) errors.end_date = dateErrors.end;
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field: keyof typeof formData, value: string | boolean) => {
    if (isReadOnly) return;
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const prepareRequestData = (): CreateInsertionOrderRequest => ({
    ...formData,
    budget: budgetData,
  });

  const saveOrUpdateInsertionOrder = async(requestData: CreateInsertionOrderRequest): Promise<string> => {
    const shouldUpdate = (mode === 'edit' && initialData?.id) || currentInsertionOrderId;

    if (shouldUpdate) {
      const idToUpdate = initialData?.id || currentInsertionOrderId!;
      await updateInsertionOrder({
        id: idToUpdate,
        data: requestData,
      }).unwrap();
      return idToUpdate;
    } else {
      const result = await createInsertionOrder(requestData).unwrap();
      setCurrentInsertionOrderId(result.id);
      return result.id;
    }
  };

  const handleSave = async() => {
    if (!validateForm()) {
      return;
    }
    try {
      const requestData = prepareRequestData();
      await saveOrUpdateInsertionOrder(requestData);
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to save insertion order:', error);
    }
  };

  const handlePreviewAndSend = async() => {
    if (!validateForm()) {
      return;
    }
    setIsGenerating(true);
    try {
      const requestData = prepareRequestData();
      const insertionOrderId = await saveOrUpdateInsertionOrder(requestData);

      const previewResult = await triggerPreview(insertionOrderId).unwrap();
      setPreviewData(previewResult);
      setIsPreviewModalOpen(true);
    } catch (error) {
      console.error('Failed to generate preview:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleApproveAndSend = async() => {
    if (!previewData?.id) {
      console.error('Cannot send insertion order: missing ID');
      return;
    }

    try {
      await sendInsertionOrder(previewData.id).unwrap();
      setIsPreviewModalOpen(false);
      onOpenChange(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Failed to send insertion order:', error);
    }
  };

  const handleBackToEdit = () => {
    setIsPreviewModalOpen(false);
  };

  const handleViewDocument = () => {
    setPreviewData(initialData ?? null);
    setIsPreviewModalOpen(true);
  };

  const getTitle = () => {
    switch (mode) {
      case 'create': return 'Create Insertion Order';
      case 'edit': return 'Edit Insertion Order';
      case 'view': return 'View Insertion Order';
      default: return 'Insertion Order';
    }
  };

  const getInputClassName = (field: string) => {
    const baseClass = "h-8 text-sm";
    const hasError = validationErrors[field];
    return hasError ? `${baseClass} border-red-500 focus:border-red-500 focus:ring-red-500` : baseClass;
  };

  const getSuggestions = (fieldName: string): string[] => {
    if (!fieldsData) return [];
    switch (fieldName) {
      case 'advertiser_name':
        return fieldsData.advertiser || [];
      case 'campaign_name':
        return fieldsData.campaign || [];
      case 'client_name':
        return fieldsData.billing_contact || [];
      case 'client_email':
        return fieldsData.billing_contact_email || [];
      case 'company_name':
        return (fieldsData.company_name || []).filter(Boolean) as string[];
      case 'agent_name':
        return fieldsData.agent_name || [];
      case 'agent_email':
        return fieldsData.agent_email || [];
      default:
        return [];
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={onOpenChange}
      >
        <DialogContent className="sm:max-w-[900px] bg-white max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {getTitle()}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Main Layout: 2 Columns */}
            <div className="flex flex-col md:flex-row gap-4">
              {/* Column 1: Company Info (1/4 width) */}
              <div className="md:w-1/4 space-y-2">
                <div className="space-y-1">
                  <label
                    htmlFor="company_name"
                    className="text-sm font-medium text-gray-700"
                  >
                    Company Name *
                  </label>
                  <AutocompleteInput
                    id="company_name"
                    value={formData.company_name || ''}
                    onChange={(value) => handleInputChange('company_name', value)}
                    placeholder="Company name"
                    suggestions={getSuggestions('company_name')}
                    className={getInputClassName('company_name')}
                    required
                    disabled={isReadOnly}
                  />
                  {validationErrors.company_name && (
                    <p className="text-red-500 text-xs mt-1">
                      {validationErrors.company_name}
                    </p>
                  )}
                </div>
                <div className="space-y-1">
                  <label
                    htmlFor="advertiser_name"
                    className="text-sm font-medium text-gray-700"
                  >
                    Advertiser Name *
                  </label>
                  <AutocompleteInput
                    id="advertiser_name"
                    value={formData.advertiser_name}
                    onChange={(value) => handleInputChange('advertiser_name', value)}
                    placeholder="Advertiser name"
                    suggestions={getSuggestions('advertiser_name')}
                    className={getInputClassName('advertiser_name')}
                    required
                    disabled={isReadOnly}
                  />
                  {validationErrors.advertiser_name && (
                    <p className="text-red-500 text-xs mt-1">
                      {validationErrors.advertiser_name}
                    </p>
                  )}
                </div>
                <div className="space-y-1">
                  <label
                    htmlFor="campaign_name"
                    className="text-sm font-medium text-gray-700"
                  >
                    Campaign Name *
                  </label>
                  <AutocompleteInput
                    id="campaign_name"
                    value={formData.campaign_name}
                    onChange={(value) => handleInputChange('campaign_name', value)}
                    placeholder="Campaign name"
                    suggestions={getSuggestions('campaign_name')}
                    className={getInputClassName('campaign_name')}
                    required
                    disabled={isReadOnly}
                  />
                  {validationErrors.campaign_name && (
                    <p className="text-red-500 text-xs mt-1">
                      {validationErrors.campaign_name}
                    </p>
                  )}
                </div>
              </div>

              {/* Vertical Divider */}
              <div className="hidden md:block w-px bg-gray-200 mx-1" />

              {/* Column 2: Billing Details (3/4 width) */}
              <div className="md:w-3/4 space-y-2 flex-col">
                <div className="flex flex-col md:flex-row justify-between gap-4">
                  {/* Sub-Column 1: Dates */}
                  <div className="flex flex-col gap-2 flex-grow md:max-w-[150px]">
                    <div className="flex-1 space-y-1">
                      <label
                        htmlFor="start_date"
                        className="text-sm font-medium text-gray-700"
                      >
                        Start Date *
                      </label>
                      <Input
                        id="start_date"
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => handleInputChange('start_date', e.target.value)}
                        className={getInputClassName('start_date')}
                        required
                        disabled={isReadOnly}
                      />
                      {validationErrors.start_date && (
                        <p className="text-red-500 text-xs mt-1">
                          {validationErrors.start_date}
                        </p>
                      )}
                    </div>
                    <div className="flex-1 space-y-1">
                      <label
                        htmlFor="end_date"
                        className="text-sm font-medium text-gray-700"
                      >
                        End Date *
                      </label>
                      <Input
                        id="end_date"
                        type="date"
                        value={formData.end_date}
                        onChange={(e) => handleInputChange('end_date', e.target.value)}
                        className={getInputClassName('end_date')}
                        required
                        disabled={isReadOnly}
                      />
                      {validationErrors.end_date && (
                        <p className="text-red-500 text-xs mt-1">
                          {validationErrors.end_date}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Vertical Divider */}
                  <div className="hidden md:block w-px bg-gray-200 mx-1" />

                  {/* Sub-Column 2: Client Details */}
                  <div className="flex flex-col gap-2 flex-grow">
                    <div className="flex-1 space-y-1">
                      <label
                        htmlFor="client_name"
                        className="text-sm font-medium text-gray-700"
                      >
                        Client Name *
                      </label>
                      <AutocompleteInput
                        id="client_name"
                        value={formData.client_name}
                        onChange={(value) => handleInputChange('client_name', value)}
                        placeholder="Client name"
                        suggestions={getSuggestions('client_name')}
                        className={getInputClassName('client_name')}
                        required
                        disabled={isReadOnly}
                      />
                      {validationErrors.client_name && (
                        <p className="text-red-500 text-xs mt-1">
                          {validationErrors.client_name}
                        </p>
                      )}
                    </div>
                    <div className="flex-1 space-y-1">
                      <label
                        htmlFor="client_email"
                        className="text-sm font-medium text-gray-700"
                      >
                        Client Email *
                      </label>
                      <AutocompleteInput
                        id="client_email"
                        type="email"
                        value={formData.client_email}
                        onChange={(value) => handleInputChange('client_email', value)}
                        placeholder="client@example.com"
                        suggestions={getSuggestions('client_email')}
                        className={getInputClassName('client_email')}
                        required
                        disabled={isReadOnly}
                      />
                      {validationErrors.client_email && (
                        <p className="text-red-500 text-xs mt-1">
                          {validationErrors.client_email}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Vertical Divider */}
                  <div className="hidden md:block w-px bg-gray-200 mx-1" />

                  {/* Sub-Column 3: Agent Details */}
                  <div className="flex flex-col gap-2 flex-grow">
                    <div className="flex-1 space-y-1">
                      <label
                        htmlFor="agent_name"
                        className="text-sm font-medium text-gray-700"
                      >
                        Internal Name *
                      </label>
                      <AutocompleteInput
                        id="agent_name"
                        value={formData.agent_name}
                        onChange={(value) => handleInputChange('agent_name', value)}
                        placeholder="Agent name"
                        suggestions={getSuggestions('agent_name')}
                        className={getInputClassName('agent_name')}
                        required
                        disabled={isReadOnly}
                      />
                      {validationErrors.agent_name && (
                        <p className="text-red-500 text-xs mt-1">
                          {validationErrors.agent_name}
                        </p>
                      )}
                    </div>
                    <div className="flex-1 space-y-1">
                      <label
                        htmlFor="agent_email"
                        className="text-sm font-medium text-gray-700"
                      >
                        Internal Email *
                      </label>
                      <AutocompleteInput
                        id="agent_email"
                        type="email"
                        value={formData.agent_email}
                        onChange={(value) => handleInputChange('agent_email', value)}
                        placeholder="agent@example.com"
                        suggestions={getSuggestions('agent_email')}
                        className={getInputClassName('agent_email')}
                        required
                        disabled={isReadOnly}
                      />
                      {validationErrors.agent_email && (
                        <p className="text-red-500 text-xs mt-1">
                          {validationErrors.agent_email}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* CC Emails */}
                <div className="space-y-1 !mt-3">
                  <div className="flex flex-col md:flex-row md:items-center md:gap-2">
                    <label
                      htmlFor="cc_emails"
                      className="text-sm font-medium text-gray-700"
                    >
                      CC Emails (comma-separated)
                    </label>
                    <span className="text-xs text-gray-500 italic">
                      Include everyone who should receive status updates
                    </span>
                  </div>
                  <Input
                    id="cc_emails"
                    value={formData.cc_emails}
                    onChange={(e) => handleInputChange('cc_emails', e.target.value)}
                    placeholder="email1@example.com, email2@example.com"
                    className={getInputClassName('cc_emails')}
                    disabled={isReadOnly}
                  />
                  {validationErrors.cc_emails && (
                    <p className="text-red-500 text-xs mt-1">
                      {validationErrors.cc_emails}
                    </p>
                  )}
                </div>

                {/* Additional Options */}
                <div className="space-y-1 !mt-3">
                  <div className="flex items-center gap-4">
                    <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                      Additional Options:
                    </label>

                    <select
                      id="logo"
                      value={formData.logo}
                      onChange={(e) => handleInputChange('logo', e.target.value)}
                      disabled={isReadOnly}
                      className="h-8 text-sm border border-gray-300 rounded-md px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="campaign_insights">
                        Campaign Insights
                      </option>
                      <option value="invisibly">
                        Invisibly
                      </option>
                    </select>

                    <div className="flex items-center space-x-2 ml-2">
                      <input
                        type="checkbox"
                        id="prebill"
                        checked={formData.prebill}
                        onChange={(e) => handleInputChange('prebill', e.target.checked)}
                        disabled={isReadOnly}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label
                        htmlFor="prebill"
                        className="text-sm text-gray-700 cursor-pointer whitespace-nowrap"
                      >
                        Prebill
                      </label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="include_sla"
                        checked={formData.include_sla}
                        onChange={(e) => handleInputChange('include_sla', e.target.checked)}
                        disabled={isReadOnly}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label
                        htmlFor="include_sla"
                        className="text-sm text-gray-700 cursor-pointer whitespace-nowrap"
                      >
                        Include SLA
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-2">
              <InsertionOrderTable
                initialData={budgetData}
                onChange={setBudgetData}
                disabled={isReadOnly}
              />
            </div>
          </div>

          <DialogFooter className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {isReadOnly ? 'Close' : 'Cancel'}
            </Button>
            {isReadOnly && initialData?.document_url && (
              <Button
                className="bg-green-1000 text-white hover:bg-green-1000/90"
                onClick={handleViewDocument}
              >
                View Document
              </Button>
            )}
            {!isReadOnly && (
              <>
                <Button
                  variant="outline"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  className="bg-green-1000 text-white hover:bg-green-1000/90"
                  onClick={handlePreviewAndSend}
                  disabled={isSaving || isLoadingPreview || isGenerating}
                >
                  {(isSaving || isLoadingPreview || isGenerating) ? 'Generating...' : 'Preview & Send'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DocumentPreviewModal
        open={isPreviewModalOpen}
        onOpenChange={setIsPreviewModalOpen}
        documentUrl={previewData?.document_url || null}
        onApproveAndSend={handleApproveAndSend}
        onBackToEdit={handleBackToEdit}
        isLoading={isSending}
        viewOnly={isReadOnly}
      />
    </>
  );
};